import { useRef } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
} from "framer-motion";
import { ArrowRight } from "lucide-react";
import LuminartLogo from "./LuminartLogo";
import { HERO_BG } from "../campaigns";
import { useSiteText } from "../lib/content";
import wordmarkLuminart from "../assets/wordmark-luminart.png";
import wordmarkProjection from "../assets/wordmark-projection.png";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// Masked line reveal driven directly by scroll progress.
const MaskedLine = ({ progress, range, className, children, testId }) => {
  const y = useTransform(progress, range, ["115%", "0%"]);
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span style={{ y }} className={`block ${className}`} data-testid={testId}>
        {children}
      </motion.span>
    </span>
  );
};

export const Hero = ({ onAssembled }) => {
  const sectionRef = useRef(null);
  const shownRef = useRef(false);
  const t = useSiteText();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  // Marble Arch — brightens and settles as the visitor scrolls.
  const imgOpacity = useTransform(scrollYProgress, [0.3, 0.62], [0.28, 0.8]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.14, 1.02]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "2%"]);

  // Logo holds, then fades + drifts away.
  const logoOpacity = useTransform(scrollYProgress, [0.26, 0.48], [1, 0]);
  const logoScale = useTransform(scrollYProgress, [0.26, 0.5], [1, 0.86]);
  const logoY = useTransform(scrollYProgress, [0.26, 0.5], [0, -56]);

  // Copy block slides in from the left.
  const copyX = useTransform(scrollYProgress, [0.4, 0.62], [-48, 0]);
  const copyOpacity = useTransform(scrollYProgress, [0.4, 0.5], [0, 1]);
  const veilOpacity = useTransform(scrollYProgress, [0.4, 0.62], [0, 1]);

  const hintOpacity = useTransform(scrollYProgress, [0, 0.1], [1, 0]);
  const ctaOpacity = useTransform(scrollYProgress, [0.62, 0.72], [0, 1]);
  const ctaY = useTransform(scrollYProgress, [0.62, 0.72], [18, 0]);

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const shouldShow = p > 0.15;
    if (shouldShow !== shownRef.current) {
      shownRef.current = shouldShow;
      onAssembled?.(shouldShow);
    }
  });

  return (
    <section
      ref={sectionRef}
      className="relative h-[260vh]"
      data-testid="hero-section"
    >
      <div className="sticky top-0 h-[100svh] w-full overflow-hidden bg-[#050505]">
        {/* Marble Arch — time-based entrance, scroll-driven brightening */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2.6, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
        >
          <motion.img
            src={HERO_BG}
            alt="Luminart projection artwork on a Brick Lane wall, London"
            style={{ opacity: imgOpacity, scale: imgScale, y: imgY }}
            className="h-full w-full object-cover object-[52%_48%]"
            data-testid="hero-marble-arch"
          />
        </motion.div>

        {/* Left veil for editorial legibility as copy arrives */}
        <motion.div
          style={{ opacity: veilOpacity }}
          className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/85 via-black/45 to-transparent"
        />

        {/* Opening logo — untouched drawing animation */}
        <motion.div
          style={{ opacity: logoOpacity, scale: logoScale, y: logoY }}
          className="pointer-events-none absolute inset-0 z-30 flex flex-col items-center justify-center"
          data-testid="hero-opening-logo"
        >
          <LuminartLogo className="w-56 md:w-72 h-auto" draw glow />
          <div className="mt-6 text-center">
            <img
              src={wordmarkLuminart}
              alt="Luminart"
              className="lum-word-1 mx-auto h-[18px] md:h-[26px] w-auto"
            />
            <img
              src={wordmarkProjection}
              alt="Projection"
              className="lum-word-2 mx-auto mt-3 h-[8px] md:h-[11px] w-auto opacity-50"
            />
          </div>
        </motion.div>

        {/* Hero copy — masked line-by-line reveal from the left */}
        <motion.div
          style={{ x: copyX, opacity: copyOpacity }}
          className="absolute inset-0 z-20 flex items-center"
        >
          <div className="px-6 md:px-12 lg:px-24 max-w-5xl">
            <MaskedLine
              progress={scrollYProgress}
              range={[0.42, 0.52]}
              className="text-[10px] md:text-xs uppercase tracking-[0.45em] text-[#c8b99d]"
              testId="hero-eyebrow"
            >
              {t("hero_eyebrow", "Projection · Mapping · Immersive Experiences")}
            </MaskedLine>
            <h2 className="mt-7 font-heading font-extralight uppercase leading-[0.98] tracking-tight text-white text-5xl sm:text-7xl lg:text-8xl">
              <MaskedLine progress={scrollYProgress} range={[0.46, 0.58]} testId="hero-headline-line-1">
                {t("hero_headline_1", "Architecture")}
              </MaskedLine>
              <MaskedLine progress={scrollYProgress} range={[0.5, 0.62]} testId="hero-headline-line-2">
                {t("hero_headline_2a", "becomes")}{" "}
                <span className="italic text-[#c8b99d]">{t("hero_headline_2b", "media.")}</span>
              </MaskedLine>
            </h2>

            <MaskedLine
              progress={scrollYProgress}
              range={[0.56, 0.68]}
              className="mt-8 max-w-xl text-base md:text-lg font-light leading-relaxed text-white/60"
              testId="hero-subheading"
            >
              {t(
                "hero_subheading",
                "Creating art with light, transforming spaces, experiences and ideas into something unforgettable."
              )}
            </MaskedLine>

            <motion.div
              style={{ opacity: ctaOpacity, y: ctaY }}
              className="mt-11 flex flex-wrap items-center gap-4"
            >
              <button
                onClick={() => scrollTo("work")}
                data-testid="hero-cta-work"
                className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#c8b99d] transition-colors duration-300"
              >
                {t("hero_cta_work", "View Our Work")}
                <ArrowRight
                  size={15}
                  className="group-hover:translate-x-1 transition-transform duration-300"
                />
              </button>
              <button
                onClick={() => scrollTo("contact")}
                data-testid="hero-cta-contact"
                className="group inline-flex items-center gap-3 border border-white/30 text-white px-8 py-4 text-xs uppercase tracking-[0.25em] hover:border-white hover:bg-white/5 transition-colors duration-300"
              >
                {t("hero_cta_contact", "Get in Touch")}
                <ArrowRight
                  size={15}
                  className="group-hover:translate-x-1 transition-transform duration-300"
                />
              </button>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll hint */}
        <motion.div
          style={{ opacity: hintOpacity }}
          className="pointer-events-none absolute bottom-10 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-3"
        >
          <span className="text-[10px] uppercase tracking-[0.4em] text-white/40">
            Scroll
          </span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
            className="w-px h-10 bg-gradient-to-b from-white/50 to-transparent"
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
